import cn from 'classnames'
import React, { memo, useState } from 'react'

import { Header, TimeStation, TripCardExpanded, TripCardToggle, FareClassesExpanded } from '@components/CompactTripCard'
import Connection from '@components/Connection'
import Money from '@components/Money'
import connectionUtils from '@lib/connection'
import { getCheapestSecondClass, getCheapestFirstClassPrice } from '@lib/fareClass'
import { useTranslation } from '@lib/i18n'
import { UpdatedMediaData } from '@queries/media'
import { useSettings } from '@queries/settings'
import { useParams } from '@stores/params'

import '@components/CompactTripCard/TripCard/styles.scss'

export interface TripCardProps {
  connection: Connection
  fareClassFilter: string | null
  isInbound: boolean
  disableFareFilter: boolean
  media?: UpdatedMediaData[]
  forcedFreeTripPrice?: boolean
  onDetailsOpen?: (connection: Connection) => void
  onSelect: (fareClass: string) => void
}

enum ExpandedCardType {
  None = 'none',
  TripCardExpanded = 'TripCardExpanded',
  FareClasses = 'FareClasses',
}

const TripCard = (props: TripCardProps) => {
  const { connection, onSelect } = props

  const [{ returnDate }] = useParams()

  const [expandedCard, setExpandedCard] = useState<ExpandedCardType>(ExpandedCardType.None)
  const [{ carrierLogo }] = useSettings()
  const { t } = useTranslation()

  const toggleTripExtendedCard = (): void => {
    setExpandedCard(
      expandedCard === ExpandedCardType.TripCardExpanded ? ExpandedCardType.None : ExpandedCardType.TripCardExpanded,
    )
  }

  const toggleFareClasses = (): void => {
    setExpandedCard(
      expandedCard === ExpandedCardType.FareClasses ? ExpandedCardType.None : ExpandedCardType.FareClasses,
    )
  }

  const { departureTime, arrivalTime, departureStation, arrivalStation, fares, cheapestFareClassCode } = connection

  const segmentsCount = connection.segments.filter(segment => !connectionUtils.isSelfTransfer(segment)).length

  const tripDirection =
    segmentsCount === 1 ? t('card.directTrip') : t('card.numberOfTransfers', { count: segmentsCount - 1 })

  const isTripCardExpanded = expandedCard === ExpandedCardType.TripCardExpanded
  const isFareClassesExpanded = expandedCard === ExpandedCardType.FareClasses

  const secondClassCheapest = getCheapestSecondClass(fares, cheapestFareClassCode)
  const secondClassCheapestPrice = connectionUtils.getPrice(connection, secondClassCheapest)

  const firstClassCheapest = getCheapestFirstClassPrice(fares)
  const firstClassCheapestprice = connectionUtils.getPrice(connection, firstClassCheapest)

  const isReturnTrip = returnDate !== null

  const onSelectHandler = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    const isInteractiveElement =
      event.target instanceof HTMLButtonElement ||
      event.target instanceof HTMLAnchorElement ||
      (event.target as HTMLElement).closest('.trip-card-toggle') ||
      (event.target as HTMLElement).closest('.carousel-arrow') ||
      (!isReturnTrip && (event.target as HTMLElement).closest('.fare-class-btn-container'))

    /* istanbul ignore if - Event handler logic for return trip fare selection
       that is covered by e2e tests */
    if (isReturnTrip && (event.target as HTMLElement).closest('.fare-class-btn-container')) {
      toggleFareClasses()
      return
    }

    /* istanbul ignore if */
    if (!isInteractiveElement) {
      toggleFareClasses()
    }
  }

  return (
    <div className="trip-card-wrapper" onClick={onSelectHandler}>
      <div
        className={cn('trip-card-container', {
          expanded: isTripCardExpanded || isFareClassesExpanded,
        })}
      >
        <div className="cell-1">
          <Header connection={connection} carrierLogo={carrierLogo} />
        </div>
        <div className="trip-info-container cell-4">
          <TimeStation station={departureStation?.name} time={departureTime} />
          <div className="trip-duration">
            <Connection.Duration
              departureTime={departureTime}
              arrivalTime={arrivalTime}
              departureTimezone={departureStation.timeZone}
              arrivalTimezone={arrivalStation.timeZone}
            />
          </div>
          <TimeStation station={arrivalStation?.name} time={arrivalTime} />
        </div>
        <div className="amenities-container cell-3">
          <div className="action-expand-card" data-tag="action-expand-card">
            <TripCardToggle
              text={tripDirection}
              isCollapsed={isTripCardExpanded}
              onClickHandler={toggleTripExtendedCard}
            />
          </div>
        </div>
        <div className="fare-class-container cell-4">
          <div className="fare-class-header-row">
            <div className="fare-class-header second">{t('compactTripCard.secondClass')}</div>
            <div className="fare-class-header">{t('compactTripCard.firstClass')}</div>
          </div>
          <div className="fare-class-boxes" onClick={toggleFareClasses}>
            <div className="fare-class-box">
              <div className="price-container">
                <div className="price-label">{t('compactTripCard.from')}</div>
                <div className="price">
                  <Money {...secondClassCheapestPrice} signed={false} />
                </div>
              </div>
              <div className="price-label">{secondClassCheapest.fareClass.name}</div>
            </div>
            <div className="fare-class-box">
              <div className="price-container">
                <div className="price-label">{t('compactTripCard.from')}</div>
                <div className="price">
                  <Money {...firstClassCheapestprice} signed={false} />
                </div>
              </div>
              <div className="price-label">{firstClassCheapest?.fareClass?.name}</div>
            </div>
          </div>
          <div className="fare-class-action-item">
            <TripCardToggle isCollapsed={isFareClassesExpanded} onClickHandler={toggleFareClasses} />
          </div>
        </div>
      </div>
      {isTripCardExpanded && (
        <div className="trip-card-expanded-wrapper">
          <TripCardExpanded connection={connection} carrierLogo={carrierLogo} />
        </div>
      )}
      {isFareClassesExpanded && (
        <div className="trip-card-expanded-wrapper">
          <FareClassesExpanded connection={connection} onSelect={onSelect} />
        </div>
      )}
    </div>
  )
}

export default memo(TripCard)
